"use client";

import { Brush } from "@visx/brush";
import type { UpdateBrush } from "@visx/brush/lib/BaseBrush";
import type { Bounds } from "@visx/brush/lib/types";
import { PatternLines } from "@visx/pattern";
import { scaleLinear, scaleTime } from "@visx/scale";
import { LinePath } from "@visx/shape";
import React, { useEffect, useMemo, useRef } from "react";
import type { BaseDataPoint, ChartMargin } from "../../../utils/charts";
import { defaultMargin } from "../../../utils/charts";

interface ChartBrushProps {
  data: BaseDataPoint[][];
  seriesMetadata: Array<{ label: string; color: string }>;
  width: number;
  height: number;
  onBrushChange: (domain: [Date, Date] | null) => void;
  brushRegion: [Date, Date] | null; // Controlled: the current zoom domain
  margin?: ChartMargin; // Optional margin to match main chart
}

/**
 * ChartBrush
 *
 * Mini overview chart with draggable brush selection for zooming into time ranges.
 * Displays simplified line representations of all series data.
 */
export const ChartBrush: React.FC<ChartBrushProps> = ({
  data,
  seriesMetadata,
  width,
  height,
  onBrushChange,
  brushRegion,
  margin = defaultMargin,
}) => {
  // biome-ignore lint/suspicious/noExplicitAny: Brush internal ref type is not exported
  const brushRef = useRef<any>(null);
  // Flag to prevent triggering onChange during programmatic updates
  const isInternalUpdateRef = useRef(false);

  // Flatten all data for domain calculation
  const allData = useMemo(() => data.flat(), [data]);

  // Create brush margin matching the main chart margin
  const brushMargin = useMemo(
    () => ({
      top: 10,
      bottom: 15,
      left: margin.left,
      right: margin.right,
    }),
    [margin]
  );

  // Create scales for mini chart
  // The xScale range should match the Brush component's physical width (which excludes margins)
  const brushWidth = width - brushMargin.left - brushMargin.right;
  const xScale = useMemo(() => {
    if (allData.length === 0) {
      return scaleTime({
        domain: [new Date(), new Date()],
        range: [0, brushWidth],
      });
    }

    const minDate = new Date(Math.min(...allData.map((d) => d.xValue.getTime())));
    const maxDate = new Date(Math.max(...allData.map((d) => d.xValue.getTime())));

    return scaleTime({
      domain: [minDate, maxDate],
      range: [0, brushWidth],
    });
  }, [allData, brushWidth]);

  const yScale = useMemo(() => {
    const yMax = allData.length > 0 ? Math.max(0, ...allData.map((d) => d.yValue)) : 1;
    return scaleLinear({
      domain: [0, yMax],
      range: [height - brushMargin.bottom, brushMargin.top],
    });
  }, [allData, height]);

  const handleBrushChange = (domain: Bounds | null) => {
    // Skip if this is an internal/programmatic update
    if (isInternalUpdateRef.current) {
      return;
    }

    if (!domain) {
      onBrushChange(null);
      return;
    }

    const { x0, x1 } = domain;

    if (x0 === x1) {
      // Single point selection, ignore
      return;
    }

    // The brush onChange gives us domain values (timestamps), not pixel coordinates
    const startDate = new Date(x0);
    const endDate = new Date(x1);

    // Validate dates
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
      return;
    }

    // Check if brush is at maximum extent (full domain)
    if (allData.length > 0) {
      const minTime = Math.min(...allData.map((d) => d.xValue.getTime()));
      const maxTime = Math.max(...allData.map((d) => d.xValue.getTime()));
      const totalRange = maxTime - minTime;
      const tolerance = totalRange * 0.05; // 5% tolerance (more generous to account for pixel constraints)

      const isFullDomain =
        Math.abs(startDate.getTime() - minTime) < tolerance && Math.abs(endDate.getTime() - maxTime) < tolerance;

      if (isFullDomain) {
        // User dragged brush to full extent, treat as reset
        onBrushChange(null);
        return;
      }
    }

    onBrushChange([startDate, endDate]);
  };

  // Calculate initial brush extent - always start with full domain
  const initialBrushExtent = useMemo(() => {
    if (allData.length === 0) return undefined;

    return {
      start: { x: 0, y: brushMargin.top },
      end: { x: brushWidth, y: height - brushMargin.bottom },
    };
  }, [brushWidth, height, allData]);

  // Sync brush to reflect current brushRegion (controlled component pattern)
  useEffect(() => {
    if (!brushRef.current || allData.length === 0) return;

    // Don't update if user is currently dragging (check if brush is active)
    const brushState = brushRef.current.state;
    if (brushState?.isBrushing || brushState?.activeHandle) {
      return;
    }

    const minTime = Math.min(...allData.map((d) => d.xValue.getTime()));
    const maxTime = Math.max(...allData.map((d) => d.xValue.getTime()));

    // Determine what the brush should show
    const targetStart = brushRegion ? brushRegion[0] : new Date(minTime);
    const targetEnd = brushRegion ? brushRegion[1] : new Date(maxTime);

    // Check if the brush is already at the target position (within 1 second tolerance)
    const currentExtent = brushRef.current.state?.extent;
    if (currentExtent) {
      const currentStart = xScale.invert(currentExtent.x0);
      const currentEnd = xScale.invert(currentExtent.x1);

      const startDiff = Math.abs(currentStart.getTime() - targetStart.getTime());
      const endDiff = Math.abs(currentEnd.getTime() - targetEnd.getTime());

      // If already at target position, skip update
      if (startDiff < 1000 && endDiff < 1000) {
        return;
      }
    }

    // Set flag to prevent onChange callback during programmatic update
    isInternalUpdateRef.current = true;

    const updater: UpdateBrush = (prevBrush) => {
      const newExtent = brushRef.current!.getExtent(
        { x: xScale(targetStart), y: brushMargin.top },
        { x: xScale(targetEnd), y: height - brushMargin.bottom }
      );

      return {
        ...prevBrush,
        start: { y: newExtent.y0, x: newExtent.x0 },
        end: { y: newExtent.y1, x: newExtent.x1 },
        extent: newExtent,
      };
    };

    brushRef.current.updateBrush(updater);

    // Reset flag after a brief delay
    setTimeout(() => {
      isInternalUpdateRef.current = false;
    }, 50);
  }, [brushRegion, xScale, height, allData]);

  const selectedBoxStyle = {
    fill: "url(#brush_pattern)",
    fillOpacity: 0.15,
    stroke: "var(--color-border-focus)",
    strokeWidth: 1.5,
    strokeOpacity: 0.3,
  };

  return (
    <div className="mt-2">
      <svg width={width} height={height}>
        <PatternLines
          id="brush_pattern"
          height={8}
          width={8}
          stroke="var(--color-text-muted)"
          strokeWidth={1}
          orientation={["diagonal"]}
        />

        {/* Group to apply left margin offset */}
        <g transform={`translate(${brushMargin.left}, 0)`}>
          {/* Render simplified line charts */}
          {data.map((seriesData, index) => (
            <LinePath
              key={`brush-series-${index}`}
              data={seriesData}
              x={(d) => xScale(d.xValue)}
              y={(d) => yScale(d.yValue)}
              stroke={seriesMetadata[index]?.color || "var(--color-gray-500)"}
              strokeWidth={1.5}
              opacity={0.8}
            />
          ))}

          <Brush
            xScale={xScale}
            yScale={yScale}
            width={brushWidth}
            height={height - brushMargin.top - brushMargin.bottom}
            margin={{
              top: brushMargin.top,
              bottom: brushMargin.bottom,
              left: 0,
              right: 0,
            }}
            handleSize={8}
            innerRef={brushRef}
            resizeTriggerAreas={["left", "right"]}
            brushDirection="horizontal"
            initialBrushPosition={initialBrushExtent}
            onChange={handleBrushChange}
            onClick={() => onBrushChange(null)}
            selectedBoxStyle={selectedBoxStyle}
            useWindowMoveEvents
          />
        </g>
      </svg>
    </div>
  );
};
